import {injectable} from 'inversify'
import type {Filter, SelectFilter} from '@/src/features/catalog/filters-v2/types'
import type {CatalogSection} from '@/src/core/domain/entities/catalog'

/**
 * Grid size for filter layout
 * Filters are displayed in a grid, and we pad the array to fit this size
 */
const FILTERS_GRID_SIZE = 16

/**
 * Transform Filters Use Case
 *
 * Transforms raw filter data for display in the catalog.
 * Responsibilities:
 * - Add category filter from subsections
 * - Pad filter array to grid size for layout
 *
 * This logic was extracted from CatalogFiltersContainer component
 * to follow Single Responsibility Principle.
 *
 * @example
 * const transformedFilters = transformFiltersUseCase.execute(filters, subsections)
 */
@injectable()
export class TransformFiltersUseCase {
  /**
   * Transform filters for display
   * @param filters - Original filter list
   * @param subsections - Category subsections to create category filter
   * @returns Transformed filters array padded to grid size
   */
  execute(filters: Filter[], subsections: CatalogSection[]): (Filter | null)[] {
    const result: (Filter | null)[] = [...filters]

    // Add category filter if subsections exist
    if (subsections.length > 0) {
      result.unshift(this.createCategoryFilter(subsections))
    }

    // Pad to grid size for layout
    return this.padToGridSize(result, FILTERS_GRID_SIZE)
  }

  /**
   * Create a category filter from subsections
   * @param subsections - Category subsections
   * @returns SelectFilter for category selection
   */
  private createCategoryFilter(subsections: CatalogSection[]): SelectFilter {
    return {
      type: 'select',
      title: 'Категория',
      code: 'category',
      multiple: false,
      values: subsections.map(section => ({
        title: section.name,
        value: section.path,
        selected: false,
        disabled: false,
      })),
    }
  }

  /**
   * Pad filter array to fit grid size
   * @param filters - Filter array to pad
   * @param gridSize - Target grid size
   * @returns Padded array with null placeholders
   */
  private padToGridSize(filters: (Filter | null)[], gridSize: number): (Filter | null)[] {
    const remainder = filters.length % gridSize
    if (remainder === 0) return filters

    const paddingSize = gridSize - remainder
    const padding: null[] = new Array(paddingSize).fill(null)
    return [...filters, ...padding]
  }
}
